import {
  bigint,
  boolean,
  check,
  index,
  jsonb,
  pgTable,
  smallint,
  timestamp,
  uuid,
  unique,
  varchar,
  uniqueIndex,
} from 'drizzle-orm/pg-core';
import { foreignKey, integer } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { driver } from '@trashlab/api/src/domains/driver/schemas';
import { recurring_service } from '@trashlab/api/src/domains/orders/schemas/recurring-service';
import {
  container_type,
  customer_category,
} from '@trashlab/api/src/domains/core/schemas/base-template';
import { AnyPgColumn } from 'drizzle-orm/pg-core';
import { defaultFields, defaultIndexes } from '@trashlab/api/drizzle/default';
import { material } from '@trashlab/api/src/domains/pricing/schemas/material';

export const route = pgTable(
  'route',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    versionId: uuid('versionId').defaultRandom().notNull().unique(),
    createdAtTs: timestamp('createdAtTs', {
      withTimezone: true,
      mode: 'string',
    })
      .defaultNow()
      .notNull(),
    updatedAtTs: timestamp('updatedAtTs', {
      withTimezone: true,
      mode: 'string',
    })
      .defaultNow()
      .notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    versionNumber: bigint('versionNumber', { mode: 'number' })
      .default(1)
      .notNull(),
    archivedAtTs: timestamp('archivedAtTs', {
      withTimezone: true,
      mode: 'string',
    }),
    tenantId: uuid('tenantId').notNull(),
    name: varchar('name', { length: 256 }).notNull(),
    lineageId: uuid('lineageId'),
    currentVersionId: uuid('currentVersionId'),
    driverId: uuid('driverId'),
    serviceType: customer_category('serviceType').notNull(),
    containerType: container_type('containerType').notNull(),
    dayOfWeek: integer('dayOfWeek').notNull(),
    isDraft: boolean('isDraft').default(false),
    /** Geofence radius in meters for autosense/autocomplete - null means use driver default */
    geofenceRadiusMeters: smallint('geofenceRadiusMeters'),
    color: varchar('color', { length: 7 }).default('#2563A6').notNull(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('route_createdAtTs_idx').on(table.createdAtTs),
      updatedAtTs_idx: index('route_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('route_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('route_versionNumber_idx').on(
        table.versionNumber,
      ),
      tenantId_idx: index('route_tenantId_idx').on(table.tenantId),
      lineageId_idx: index('route_lineageId_idx').on(table.lineageId),
      currentVersionId_idx: index('route_currentVersionId_idx').on(
        table.currentVersionId,
      ),
      containerType_idx: index('route_containerType_idx').on(
        table.containerType,
      ),
      // ENG-5963: Composite indexes for faster route list filtering
      route_tenant_day_updated_active_idx: index(
        'route_tenant_day_updated_active_idx',
      )
        .on(table.tenantId, table.dayOfWeek, table.updatedAtTs.desc())
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.isDraft} = false`,
        ),
      route_tenant_day_updated_archived_idx: index(
        'route_tenant_day_updated_archived_idx',
      )
        .on(table.tenantId, table.dayOfWeek, table.updatedAtTs.desc())
        .where(sql`${table.archivedAtTs} IS NOT NULL`),
      route_tenant_container_day_updated_active_idx: index(
        'route_tenant_container_day_updated_active_idx',
      )
        .on(
          table.tenantId,
          table.containerType,
          table.dayOfWeek,
          table.updatedAtTs.desc(),
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.isDraft} = false`,
        ),
      route_order_filter_name_trgm_idx: index('route_order_filter_name_trgm_idx')
        .using('gin', table.name.op('gin_trgm_ops'))
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.isDraft} = false`,
        ),
      route_order_filter_tenant_day_name_id_idx: index(
        'route_order_filter_tenant_day_name_id_idx',
      )
        .on(table.tenantId, table.dayOfWeek, table.name, table.id)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.isDraft} = false`,
        ),

      driverId_idx: index('route_driverId_idx').on(table.driverId),

      routeColorFormatCheck: check(
        'route_color_format_chk',
        sql`${table.color} ~ '^#[0-9A-F]{6}$'`,
      ),

      route_driverId_fkey: foreignKey({
        columns: [table.driverId],
        foreignColumns: [driver.id],
        name: 'route_driverId_fkey',
      }),
    };
  },
);

export const routeHistory = pgTable(
  'route_history',
  {
    id: uuid('id'),
    versionId: uuid('versionId').primaryKey(),
    createdAtTs: timestamp('createdAtTs', {
      withTimezone: true,
      mode: 'string',
    }),
    updatedAtTs: timestamp('updatedAtTs', {
      withTimezone: true,
      mode: 'string',
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    versionNumber: bigint('versionNumber', { mode: 'number' }),
    tenantId: uuid('tenantId'),
    archivedAtTs: timestamp('archivedAtTs', {
      withTimezone: true,
      mode: 'string',
    }),
    name: varchar('name', { length: 256 }),
    lineageId: uuid('lineageId'),
    currentVersionId: uuid('currentVersionId'),
    driverId: uuid('driverId'),
    serviceType: customer_category('serviceType'),
    containerType: container_type('containerType'),
    dayOfWeek: integer('dayOfWeek'),
    isDraft: boolean('isDraft'),
    geofenceRadiusMeters: smallint('geofenceRadiusMeters'),
    color: varchar('color', { length: 7 }).default('#2563A6').notNull(),
  },
  (table) => ({
    routeHistoryColorFormatCheck: check(
      'route_history_color_format_chk',
      sql`${table.color} ~ '^#[0-9A-F]{6}$'`,
    ),
  }),
);

export const routeAssignment = pgTable(
  'route_assignment',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    versionId: uuid('versionId').defaultRandom().notNull().unique(),
    createdAtTs: timestamp('createdAtTs', {
      withTimezone: true,
      mode: 'string',
    })
      .defaultNow()
      .notNull(),
    updatedAtTs: timestamp('updatedAtTs', {
      withTimezone: true,
      mode: 'string',
    })
      .defaultNow()
      .notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    versionNumber: bigint('versionNumber', { mode: 'number' })
      .default(1)
      .notNull(),
    tenantId: uuid('tenantId').notNull(),
    routeId: uuid('routeId').notNull(),
    recurringServiceId: uuid('recurringServiceId').notNull(),
    visitOrder: smallint('visitOrder'),
    isSuspended: boolean('isSuspended').default(false),
    /** Note for this stop, typically containing placement/container info */
    isDriverNoteUrgent: boolean('isDriverNoteUrgent').notNull().default(false),
    stopNote: varchar('stopNote', { length: 500 }),
  },
  (table) => {
    return {
      createdAtTs_idx: index('route_assignment_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      updatedAtTs_idx: index('route_assignment_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('route_assignment_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('route_assignment_versionNumber_idx').on(
        table.versionNumber,
      ),
      tenantId_idx: index('route_assignment_tenantId_idx').on(table.tenantId),
      routeId_idx: index('route_assignment_routeId_idx').on(table.routeId),
      recurringServiceId_idx: index(
        'route_assignment_recurringServiceId_idx',
      ).on(table.recurringServiceId),
      // Optimized index for open rentals query route assignments subquery
      tenantRecurringServiceIdx: index(
        'route_assignment_tenant_recurring_service_idx',
      ).on(table.tenantId, table.recurringServiceId),
      // ENG-5963: Composite indexes for route stops ordering
      route_assignment_route_visit_order_idx: index(
        'route_assignment_route_visit_order_idx',
      ).on(table.routeId, table.visitOrder),

      route_assignment_tenant_route_visit_order_idx: index(
        'route_assignment_tenant_route_visit_order_idx',
      ).on(table.tenantId, table.routeId, table.visitOrder),
      route_assignment_order_filter_active_idx: index(
        'route_assignment_order_filter_active_idx',
      )
        .on(table.tenantId, table.routeId, table.recurringServiceId)
        .where(sql`${table.isSuspended} = false`),

      route_assignment_routeId_fkey: foreignKey({
        columns: [table.routeId],
        foreignColumns: [route.id],
        name: 'route_assignment_routeId_fkey',
      }),
      route_assignment_recurringServiceId_fkey: foreignKey({
        columns: [table.recurringServiceId],
        foreignColumns: [recurring_service.id as AnyPgColumn],
        name: 'route_assignment_recurringServiceId_fkey',
      }),
      uniqueRouteIdAndRecurringServiceId: unique(
        'uniqueRouteIdAndRecurringServiceId',
      ).on(table.routeId, table.recurringServiceId),
    };
  },
);

export const routeAssignmentHistory = pgTable('route_assignment_history', {
  id: uuid('id'),
  versionId: uuid('versionId').primaryKey(),
  createdAtTs: timestamp('createdAtTs', {
    withTimezone: true,
    mode: 'string',
  }),
  updatedAtTs: timestamp('updatedAtTs', {
    withTimezone: true,
    mode: 'string',
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  versionNumber: bigint('versionNumber', { mode: 'number' }),
  tenantId: uuid('tenantId'),
  routeId: uuid('routeId'),
  recurringServiceId: uuid('recurringServiceId'),
  visitOrder: smallint('visitOrder'),
  isSuspended: boolean('isSuspended'),
  isDriverNoteUrgent: boolean('isDriverNoteUrgent'),
  stopNote: varchar('stopNote', { length: 500 }),
});

export const routeSegment = pgTable(
  'route_segment',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    versionId: uuid('versionId').defaultRandom().notNull().unique(),
    createdAtTs: timestamp('createdAtTs', {
      withTimezone: true,
      mode: 'string',
    })
      .defaultNow()
      .notNull(),
    updatedAtTs: timestamp('updatedAtTs', {
      withTimezone: true,
      mode: 'string',
    })
      .defaultNow()
      .notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    versionNumber: bigint('versionNumber', { mode: 'number' })
      .default(1)
      .notNull(),
    tenantId: uuid('tenantId').notNull(),
    start: jsonb('start').notNull(),
    end: jsonb('end').notNull(),
    key: varchar('key', { length: 256 }).notNull(),
    geoJson: jsonb('geoJson').notNull(),
    travelTime: integer('travelTime').notNull(),
    travelDistance: integer('travelDistance').notNull(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('route_segment_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      updatedAtTs_idx: index('route_segment_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('route_segment_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('route_segment_versionNumber_idx').on(
        table.versionNumber,
      ),
      tenantId_idx: index('route_segment_tenantId_idx').on(table.tenantId),
      key_idx: index('route_segment_key_idx').on(table.key),
      tenantKeyIdx: index('route_segment_tenant_key_idx').on(
        table.tenantId,
        table.key,
      ),
    };
  },
);

export const routeSegmentHistory = pgTable('route_segment_history', {
  id: uuid('id'),
  versionId: uuid('versionId').primaryKey(),
  createdAtTs: timestamp('createdAtTs', {
    withTimezone: true,
    mode: 'string',
  }),
  updatedAtTs: timestamp('updatedAtTs', {
    withTimezone: true,
    mode: 'string',
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  versionNumber: bigint('versionNumber', { mode: 'number' }),
  tenantId: uuid('tenantId'),
  start: jsonb('start'),
  end: jsonb('end'),
  key: varchar('key', { length: 256 }),
  geoJson: jsonb('geoJson'),
  travelTime: integer('travelTime'),
  travelDistance: integer('travelDistance'),
});

export type RouteSchema = typeof route.$inferSelect;
export type RouteSegmentSchema = typeof routeSegment.$inferSelect;
export type RouteAssignmentSchema = typeof routeAssignment.$inferSelect;

export const routeMaterial = pgTable(
  'route_material',
  {
    ...defaultFields.fieldsWithTenantId(),
    routeId: uuid('routeId').notNull(),
    materialId: uuid('materialId').notNull(),
  },
  (table) => {
    return {
      ...defaultIndexes.indexesWithTenantId('route_material', table),
      routeIdMaterialIdUniqueIdx: uniqueIndex(
        'route_material_routeId_materialId_unique_idx',
      ).on(table.routeId, table.materialId),
      route_material_routeId_fkey: foreignKey({
        columns: [table.routeId],
        foreignColumns: [route.id],
        name: 'route_material_routeId_fkey',
      }),
      route_material_materialId_fkey: foreignKey({
        columns: [table.materialId],
        foreignColumns: [material.id],
        name: 'route_material_materialId_fkey',
      }),
    };
  },
);
